import React, { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { UnderlineTabs } from '@/components/ui/underline-tabs'
import PageHeader from '@/components/PageHeader'
import { useAuthStore } from '@/stores/authStore'
import { apiService } from '@/services/api'
import { formatNumberSmart } from '@/lib/utils'
import {
  Search,
  Loader2,
  AlertCircle,
  ClipboardList,
  Utensils,
  Dumbbell
} from 'lucide-react'

const ClientProgramArchive = () => {
  const { companyUrl } = useAuthStore()
  const [programData, setProgramData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('all')

  useEffect(() => {
    const fetchProgramArchive = async () => {
      if (!companyUrl) return

      setLoading(true)
      setError(null)

      try {
        const result = await apiService.fetchProgramArchive(companyUrl)
        
        if (result.success) {
          setProgramData(result.data)
        } else {
          setError(result.error || 'Failed to fetch program archive')
        }
      } catch (err) {
        setError('Failed to fetch program archive data')
        console.error('Program archive fetch error:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchProgramArchive()
  }, [companyUrl])

  const filteredPrograms = programData?.programswithgoals?.filter(program => {
    const matchesSearch = program.name.toLowerCase().includes(searchTerm.toLowerCase())
    if (selectedFilter === 'all') return matchesSearch
    
    const programs = program.programs || []
    const isActive = programs.some(session => !session.deactivated_date)
    const isCompleted = programs.length > 0 && programs.every(session => session.deactivated_date)
    
    if (selectedFilter === 'active' && isActive) return matchesSearch
    if (selectedFilter === 'completed' && isCompleted) return matchesSearch
    if (selectedFilter === 'paused' && !isActive && !isCompleted) return matchesSearch
    
    return false
  }) || []

  const stats = programData?.programswithgoals ? {
    total: programData.programswithgoals.length,
    completed: programData.programswithgoals.filter(p => {
      const programs = p.programs || []
      return programs.length > 0 && programs.every(session => session.deactivated_date)
    }).length,
    active: programData.programswithgoals.filter(p => {
      const programs = p.programs || []
      return programs.some(session => !session.deactivated_date)
    }).length,
    totalWeightEntries: programData.programswithgoals.reduce((acc, p) => 
      acc + (p.programs || []).reduce((sessionAcc, session) => sessionAcc + (session.weights || []).length, 0), 0
    )
  } : { total: 0, completed: 0, active: 0, totalWeightEntries: 0 }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })
  }

  const getProgramStatus = (program) => {
    const programs = program.programs || []
    const isActive = programs.some(session => !session.deactivated_date)
    const isCompleted = programs.length > 0 && programs.every(session => session.deactivated_date)

    if (isActive) return { label: 'Active', color: 'border-transparent bg-emerald-50 text-emerald-700' }
    if (isCompleted) return { label: 'Completed', color: 'border-transparent bg-accent text-blue-800' }
    return { label: 'Paused', color: 'border-transparent bg-amber-50 text-amber-700' }
  }

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <p className="text-sm">Loading your archive…</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="mb-3 h-8 w-8 text-red-600" />
          <h3 className="mb-1 text-base font-semibold text-red-900">Something went wrong</h3>
          <p className="mb-5 max-w-md text-sm text-red-700">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    )
  }

  const filterTabs = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'completed', label: 'Completed' },
  ]

  return (
    <div className="app-page pb-12">
      <PageHeader
        title="Program archive"
        subtitle={`${stats.total} programs · ${stats.active} active · ${stats.totalWeightEntries} measurements`}
      />

      <UnderlineTabs
        tabs={filterTabs}
        value={selectedFilter}
        onChange={setSelectedFilter}
        label="Filter programs"
        actions={
          <div className="relative w-56">
            <label htmlFor="archive-search" className="sr-only">Search programs</label>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              id="archive-search"
              type="search"
              placeholder="Search programs"
              className="h-9 pl-9 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        }
      />

      <div className="space-y-4">
        {filteredPrograms.length > 0 ? (
          filteredPrograms.map((program) => (
            <Card key={program.id} className="overflow-hidden">
              <div className="flex flex-col justify-between gap-3 border-b border-slate-200 px-4 py-3 md:flex-row md:items-center">
                <div className="min-w-0">
                  <h3 className="truncate text-[15px] font-semibold text-slate-900">{program.name}</h3>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {formatDate(program.start_date)} to {formatDate(program.estimated_end_date)}
                  </p>
                </div>
                <Badge variant="outline" className={getProgramStatus(program).color}>
                  {getProgramStatus(program).label}
                </Badge>
              </div>

              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-b border-slate-200 bg-background">
                        <th className="h-10 px-4 text-xs font-semibold text-slate-500">Sessions</th>
                        <th className="h-10 px-4 text-right text-xs font-semibold text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {program.programs?.map((session) => (
                        <tr key={session.id} className="border-b border-slate-100 last:border-b-0 hover:bg-background">
                          <td className="px-4 py-3">
                            <div className="text-sm font-semibold text-slate-900">{session.name}</div>
                            <div className="text-xs text-slate-500">{formatDate(session.program_start_date)}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!session.weights || session.weights.length === 0}
                              >
                                <ClipboardList className="mr-1.5 h-3.5 w-3.5" />
                                Measurements ({session.weights?.length || 0})
                              </Button>
                              <Button variant="outline" size="sm">
                                <Utensils className="mr-1.5 h-3.5 w-3.5" />
                                Nutrition
                              </Button>
                              <Button variant="outline" size="sm">
                                <Dumbbell className="mr-1.5 h-3.5 w-3.5" />
                                Exercise
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!program.programs || program.programs.length === 0) && (
                        <tr>
                          <td colSpan="2" className="px-4 py-8 text-center text-sm text-slate-500">
                            No sessions found for this program.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center rounded-[14px] border border-dashed border-slate-200 py-16">
            <h3 className="mb-1 text-base font-semibold text-slate-900">No programs found</h3>
            <p className="max-w-xs text-center text-sm text-slate-500">
              We couldn&apos;t find any programs matching your search or filters.
            </p>
            {searchTerm && (
              <Button variant="link" className="mt-2" onClick={() => setSearchTerm('')}>
                Clear search
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientProgramArchive
